export const insightsServiceImplFactory = ({
  insightsRepository,
}: {
  insightsRepository: IInsightsRepository;
}) => {
  const getTOCAllowedMonthsService: IGetTOCAllowedMonthsService = async ({
    clinicId,
  }) => {
    try {
      const response = await insightsRepository.getTOCAllowedMonths({
        clinic_id: clinicId,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getTOPAllowedMonthsService: IGetTOPAllowedMonthsService = async ({
    clinicId,
  }) => {
    try {
      const response = await insightsRepository.getTOPAllowedMonths({
        clinic_id: clinicId,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getTOCService: IGetTOCService = async ({
    clinicId,
    calendar_months,
  }) => {
    try {
      const response = await insightsRepository.getTOCAPI({
        clinic_id: clinicId,
        calendar_months,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getTOPService: IGetTOPService = async ({
    clinicId,
    calendar_months,
  }) => {
    try {
      const response = await insightsRepository.getTOPAPI({
        clinic_id: clinicId,
        calendar_months,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getTOPPreventedSummaryService: IGetTOPPreventedSummaryService = async ({
    clinicId,
  }) => {
    try {
      const response = await insightsRepository.getTOPPreventedSummaryAPI({
        clinic_id: clinicId,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getTOCRootCauseService: IGetTOCRootCauseService = async ({
    clinicId,
    toEventId,
  }) => {
    try {
      const response = await insightsRepository.getTOCRootCauseAPI({
        clinic_id: clinicId,
        TO_EVENT_ID: toEventId,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getEventNoteService: IGetEventNoteService = async ({
    clinicId,
    toEventId,
  }) => {
    try {
      const response = await insightsRepository.getEventNoteAPI({
        clinic_id: clinicId,
        TO_EVENT_ID: toEventId,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const saveEventNoteService: ISaveEventNoteService = async ({
    clinicId,
    toEventId,
    note,
  }) => {
    try {
      const response = await insightsRepository.putEventNoteAPI({
        clinic_id: clinicId,
        TO_EVENT_ID: toEventId,
        note,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getDischargeAllowedMonthsService: IGetDischargeAllowedMonthsService =
    async ({ clinicId }) => {
      try {
        const response = await insightsRepository.getDischargeAllowedMonths({
          clinic_id: clinicId,
        });

        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getDischargeService: IGetDischargeService = async ({
    clinicId,
    calendar_months,
  }) => {
    try {
      const response = await insightsRepository.getDischargeAPI({
        clinic_id: clinicId,
        calendar_months,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getOutcomesFacilitiesService: IGetOutcomesFacilitiesService = async ({
    clinicId,
    clinicIds,
  }) => {
    try {
      const response = await insightsRepository.getOutcomesFacilitiesAPI({
        clinic_id: clinicId,
        clinic_ids: clinicIds,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getOutcomesAvailablePeriodsService: IGetOutcomesAvailablePeriodsService =
    async ({ clinicId, metricType, filterClinicId }) => {
      try {
        const response =
          await insightsRepository.getOutcomesAvailablePeriodsAPI({
            clinic_id: clinicId,
            metric_type: metricType,
            filter_clinic_id: filterClinicId,
          });

        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getOutcomesTimeSeriesService: IGetOutcomesTimeSeriesService = async ({
    clinicId,
    metricType,
    normalization,
    patientType,
    trendType,
    startMonth,
    endMonth,
    monthsBefore,
    monthsAfter,
    clinicIds,
    regions,
    states,
    excludeClinicIds,
  }) => {
    try {
      const response = await insightsRepository.getOutcomesTimeSeriesAPI({
        clinic_id: clinicId,
        metric_type: metricType,
        normalization,
        patient_type: patientType,
        trend_type: trendType,
        start_month: startMonth,
        end_month: endMonth,
        months_before: monthsBefore,
        months_after: monthsAfter,
        clinic_ids: clinicIds,
        regions,
        states,
        exclude_clinic_ids: excludeClinicIds,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getOutcomesFilterOptionsService: IGetOutcomesFilterOptionsService =
    async ({ clinicId }) => {
      try {
        const response = await insightsRepository.getOutcomesFilterOptionsAPI({
          clinic_id: clinicId,
        });

        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getDashboardOverviewService: IGetDashboardOverviewService = async ({
    clinicId,
    startDt,
    endDt,
  }) => {
    try {
      const response = await insightsRepository.getDashboardOverviewAPI({
        clinic_id: clinicId,
        start_dt: startDt,
        end_dt: endDt,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getFacilityNeoCostService: IGetFacilityNeoCostService = async ({
    clinicId,
    startDt,
    endDt,
  }) => {
    try {
      const response = await insightsRepository.getFacilityNeoCostAPI({
        clinic_id: clinicId,
        start_dt: startDt,
        end_dt: endDt,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getMyNeoCostService: IGetMyNeoCostService = async ({
    startDt,
    endDt,
  }) => {
    try {
      const response = await insightsRepository.getMyNeoCostAPI({
        start_dt: startDt,
        end_dt: endDt,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getMyNeoCreditsService: IGetMyNeoCreditsService = async (payload) => {
    try {
      const response = await insightsRepository.getMyNeoCreditsAPI(
        payload?.clinicId ? { clinic_id: payload.clinicId } : undefined
      );
      // The transport resolves a failed GET with its error body (FastAPI
      // `{ detail }`) or undefined; don't present that as a credit balance.
      if (typeof response?.balance_usd !== "number") {
        const detail = (response as { detail?: unknown } | undefined)?.detail;
        return {
          success: false,
          error: {
            message:
              typeof detail === "string" ? detail : "Could not load credits.",
          },
        };
      }
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  return {
    getTOCService,
    getTOPService,
    getTOPPreventedSummaryService,
    getTOCAllowedMonthsService,
    getTOPAllowedMonthsService,
    getTOCRootCauseService,
    getDischargeAllowedMonthsService,
    getDischargeService,
    getOutcomesFacilitiesService,
    getOutcomesAvailablePeriodsService,
    getOutcomesTimeSeriesService,
    getOutcomesFilterOptionsService,
    getDashboardOverviewService,
    getFacilityNeoCostService,
    getMyNeoCostService,
    getMyNeoCreditsService,
    getEventNoteService,
    saveEventNoteService,
  };
};
